import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import Reveal from "../components/Reveal";
import { PROJECTS } from "../data/projects";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "kitchen", label: "Kitchen" },
  { id: "bathroom", label: "Bathroom" },
  { id: "deck", label: "Deck" },
  { id: "painting", label: "Painting" },
];

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const visible = PROJECTS.filter((p) => filter === "all" || p.category === filter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="border-t border-line bg-surface/40">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">Selected work</p>
          <div className="mt-4 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <h2 className="max-w-xl font-display text-4xl font-semibold tracking-tight text-cream sm:text-5xl">
              Proof, poured in concrete &amp; copper.
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`portfolio-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full px-5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    filter === f.id
                      ? "bg-copper text-ink"
                      : "border border-line text-cream/60 hover:border-copper hover:text-copper"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/projects/${p.slug}`}
                  data-testid={`portfolio-card-${p.id}`}
                  className="group block overflow-hidden rounded-2xl border border-line bg-surface transition-transform duration-500 hover:-translate-y-1.5"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-copper backdrop-blur-md">
                      {p.category_label}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-4 p-6">
                    <div>
                      <h3 className="font-display text-xl font-semibold tracking-tight text-cream transition-colors group-hover:text-copper">
                        {p.title}
                      </h3>
                      <p className="mt-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-cream/50">
                        <MapPin size={12} className="text-copper" /> {p.location} — {p.year}
                      </p>
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line text-cream/60 transition-colors duration-300 group-hover:border-copper group-hover:bg-copper group-hover:text-ink">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
